/**
 * Cloud POS RBAC aligned with GET Authenticate/permission-catalog and login PermissionGroups.
 */

export type PermissionGroupRow = { group: string; permissions: string[] };

export type CloudPosPermSpec =
  | { group: string; permission: string }
  /** Any permission within the catalog group (e.g. any inventory module). */
  | { group: string; anyInGroup: true };

export function parsePermissionGroupsFromStorage(): PermissionGroupRow[] | null {
  try {
    const raw = sessionStorage.getItem('impersonatePermissionGroups') || localStorage.getItem("permissionGroups");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.map(normalizeGroupRow).filter((g) => g.group);
  } catch {
    return null;
  }
}

function normalizeGroupRow(g: unknown): PermissionGroupRow {
  const o = g as Record<string, unknown>;
  const group = String(o.group ?? o.Group ?? "");
  const permsRaw = o.permissions ?? o.Permissions ?? [];
  const permissions = Array.isArray(permsRaw)
    ? permsRaw.map((p) => String(p)).filter(Boolean)
    : [];
  return { group, permissions };
}

export function buildPermissionLookup(groups: PermissionGroupRow[]): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  for (const g of groups) {
    if (!g.group) continue;
    if (!map.has(g.group)) map.set(g.group, new Set());
    const set = map.get(g.group)!;
    for (const p of g.permissions) {
      set.add(p);
      if (g.group === "StaffSecurity" && p === "EmployeeManagement") {
        set.add("UserManagement");
      }
    }
  }
  return map;
}

export function hasPermission(
  lookup: Map<string, Set<string>>,
  group: string,
  permission: string
): boolean {
  const set = lookup.get(group);
  if (!set) return false;
  if (set.has(permission)) return true;
  if (group === "StaffSecurity" && permission === "UserManagement" && set.has("EmployeeManagement")) {
    return true;
  }
  return false;
}

export function hasAnyPermissionInGroup(lookup: Map<string, Set<string>>, group: string): boolean {
  const set = lookup.get(group);
  return !!set && set.size > 0;
}

/**
 * Legacy sessions / developer: do not lock the UI when PermissionGroups were not persisted.
 */
export function shouldBypassCloudPosPermissionChecks(groupsFromStorage: PermissionGroupRow[] | null): boolean {
  try {
    const rolesRaw = localStorage.getItem("userRoles") || "";
    const roles = rolesRaw.split(",").map((r) => r.trim().toLowerCase()).filter(Boolean);
    if (roles.includes("developer")) return true;
    if (roles.includes("storeowner")) return true;
    if (roles.includes("companyadmin")) return true;
  } catch {
    /* ignore */
  }
  if (groupsFromStorage === null) return true;
  return false;
}

export function meetsCloudPosPermission(
  lookup: Map<string, Set<string>>,
  bypass: boolean,
  spec: CloudPosPermSpec | undefined
): boolean {
  if (!spec) return true;
  if (bypass) return true;
  if ("anyInGroup" in spec && spec.anyInGroup) return hasAnyPermissionInGroup(lookup, spec.group);
  if ("permission" in spec) return hasPermission(lookup, spec.group, spec.permission);
  return true;
}

export function canAccessSetupHub(
  lookup: Map<string, Set<string>>,
  bypass: boolean,
  setupCardPerms: ReadonlyArray<{ group: string; permission: string }>
): boolean {
  if (bypass) return true;
  if (setupCardPerms.length === 0) return true;
  return setupCardPerms.some(({ group, permission }) => hasPermission(lookup, group, permission));
}

/** POS terminal card has `path: null`; use dedicated perm. */
export const SETUP_POS_TERMINAL_PERM = { group: "PosOperations", permission: "PosTerminal" } as const;

/**
 * Barcode Stock Audit permission group.
 *
 * These mirror `Domain/Entities/Permissions/CloudPosPermissionCatalog.cs` exactly — the
 * server is the authority, silently drops any key not in its catalog, and re-checks every
 * one of these on the API side (StockAuditController.HasStockAuditPermissionAsync). The
 * constants exist so pages never hand-type the group/key strings.
 */
export const STOCK_AUDIT_GROUP = "StockAudit";

export const STOCK_AUDIT_PERMISSIONS = {
  /** Routes: list, detail, lines, scans, unknown barcodes, variance report, export, events. */
  view: "StockAuditView",
  /** Create the audit and take its (irreversible) stock snapshot. */
  create: "StockAuditCreate",
  /** Scan / count / manual count / void a scan / complete counting / submit for review. */
  scan: "StockAuditScan",
  /** Review variances, bulk review, resolve unknown barcodes, manage pack barcodes. */
  review: "StockAuditReview",
  /** Approve, reject, reopen. */
  approve: "StockAuditApprove",
  /** Finalize — the only path that posts stock and GL adjustments. */
  finalize: "StockAuditFinalize",
  /** Cancel a non-finalized audit. */
  cancel: "StockAuditCancel",
} as const;

/**
 * Stock Manual Consumption permission group — stock used up WITHOUT a sale (service use,
 * internal use, damage, expiry, samples).
 *
 * Mirrors `Domain/Entities/Permissions/CloudPosPermissionCatalog.cs` group "StockConsumption"
 * exactly. The server re-checks every one of these in
 * StockConsumptionController.HasPermissionAsync, so hiding a button here is convenience only.
 */
export const STOCK_CONSUMPTION_GROUP = "StockConsumption";

export const STOCK_CONSUMPTION_PERMISSIONS = {
  /** List, details and the report. */
  view: "StockConsumptionView",
  /** Record a consumption — this is what actually deducts stock. */
  create: "StockConsumptionCreate",
  /** Edit — the server reverses the revision in effect and re-applies the new one. */
  edit: "StockConsumptionEdit",
  /** Cancel — restores the stock, keeps the document. */
  cancel: "StockConsumptionCancel",
  /** Soft-delete a CANCELLED document (a completed one can never be deleted). */
  delete: "StockConsumptionDelete",
} as const;

/**
 * Exchange module permission group (mirrors CloudPosPermissionCatalog.cs "Exchange"; the
 * server re-checks every key in ExchangeController.HasExchangePermissionAsync).
 */
export const EXCHANGE_GROUP = "Exchange";
export const EXCHANGE_PERMISSIONS = {
  /** History list, details, report. */
  view: "ExchangeView",
  /** The exchange terminal (quote + create). */
  create: "ExchangeCreate",
  /** Approve an exchange whose difference is owed back to the customer. */
  refund: "ExchangeRefund",
  /** Cancel (reverse) a completed exchange. */
  cancel: "ExchangeCancel",
  /** Exchange Dashboard (analytics). */
  dashboard: "ExchangeDashboard",
  /** Manage the store's exchange policy (Setup → Exchange → Exchange Rules). */
  rules: "ExchangeRules",
} as const;

/**
 * Deals module permission group (Setup → Deals). Mirrors CloudPosPermissionCatalog.cs "Deals";
 * the server re-checks every key in DealsController.HasPermissionAsync, so hiding a button here
 * is convenience only. Creating / editing one deal stays on the item screen.
 */
export const DEALS_GROUP = "Deals";
export const DEALS_PERMISSIONS = {
  /** The deals list and deal details. */
  view: "DealsView",
  /** Import deals from Excel (template download included). */
  import: "DealsImport",
  /** Export deals to Excel. */
  export: "DealsExport",
} as const;

/**
 * Opening balances of the payment accounts live on the Owner Transactions screen (a tab), so
 * SEEING them is that screen's permission; adding / editing / removing needs this key.
 * Mirrors CloudPosPermissionCatalog.cs "FinancialManagement"; the server re-checks it.
 */
export const OPENING_BALANCE_MANAGE_PERMISSION = { group: "FinancialManagement", permission: "OpeningBalancesManage" } as const;

/**
 * Cash management — Setup → Cash Management (confirm cash handovers, where the cash is),
 * Reports → Cash Handover Report, and the desktop's Operations → Cash Handover. Mirrors
 * CloudPosPermissionCatalog.cs "CashManagement". The server re-checks every key in
 * CashHandoverController and also gives the shared till roles (Cashier / Staff / Manager)
 * defaults that are NOT in the permission groups, so the pages ask the server
 * (`CashHandover/my-permissions`) what the user may do; these constants only drive the Setup card.
 */
export const CASH_MANAGEMENT_GROUP = "CashManagement";
export const CASH_MANAGEMENT_PERMISSIONS = {
  /** Hand a closed shift's cash over (the desktop till). */
  handover: "CashHandoverCreate",
  /** The Cash Management screen: balances and handovers. */
  view: "CashManagementView",
  /** Confirm / reject / reverse handovers (implies view on the server). */
  receive: "CashHandoverReceive",
  /** Cash Handover Report. */
  reports: "CashReports",
  /** Send cash (float) to a branch (implies view on the server). */
  float: "CashFloatSend",
} as const;

/**
 * Setup dashboard card targets → catalog permission (paths matched against `card.path`).
 * Must stay in sync with `Setup/index.tsx` navigation targets.
 */
export const SETUP_ROUTE_PERMISSION: Record<string, { group: string; permission: string }> = {
  "/management/setup/business/profile": { group: "QuickSetup", permission: "BusinessProfile" },
  "/management/setup/system/allbranches": { group: "QuickSetup", permission: "BranchSetup" },
  "/management/setup/master/rolelist": { group: "QuickSetup", permission: "UserRoles" },
  "/management/setup/system/allcategory": { group: "InventoryProducts", permission: "ProductCategories" },
  "/management/setup/system/allunits": { group: "InventoryProducts", permission: "Units" },
  "/management/setup/system/allsuppliers": { group: "InventoryProducts", permission: "Suppliers" },
  "/management/setup/opening-stock": { group: "InventoryProducts", permission: "OpeningStock" },
  // Variant purchasing is a purchase-side capability — it reuses the existing catalog key so
  // custom roles can actually be granted it (unlike the "OpeningStock" key above, which is not in
  // CloudPosPermissionCatalog.cs, so IsValidPermission rejects it and only bypass roles see it).
  "/management/setup/variant-purchasing": { group: "InventoryProducts", permission: "PurchaseInventory" },
  // Bulk variant creation writes the item catalog.
  "/management/setup/bulk-variant-items": { group: "InventoryProducts", permission: "Items" },
  // Recipe Management keeps the original "Production" key (every role that has it keeps its
  // recipes); Production Orders is its own module and permission. A role saved before the split
  // is granted Production Orders by the server (CloudPosPermissionClaimHelper.WithLegacyImplications).
  "/production": { group: "InventoryProducts", permission: "Production" },
  "/production-orders": { group: "InventoryProducts", permission: "ProductionOrders" },
  "/reports/recipe-mapping": { group: "InventoryProducts", permission: "Production" },
  "/settings": { group: "InventoryProducts", permission: "CompanySettings" },
  // Setup → "Invoice Designer" card (deep link to the Invoice tab; same permission as /settings)
  "/settings?tab=invoice": { group: "InventoryProducts", permission: "CompanySettings" },
  "/reports/labels": { group: "InventoryProducts", permission: "LabelPrinting" },
  "/management/setup/restaurants/floors": { group: "PosOperations", permission: "FloorsRestaurants" },
  "/management/setup/restaurants/tables": { group: "PosOperations", permission: "TablesRestaurants" },
  "/management/setup/restaurants/table-qr-codes": { group: "PosOperations", permission: "QrOrdering" },
  // Bookings reuses the Customers permission (appointment desk = customer-facing POS work);
  // visibility is additionally gated by store type in Setup/index.tsx.
  "/management/setup/bookings": { group: "PosOperations", permission: "Customers" },
  "/management/setup/system/allcustomers": { group: "PosOperations", permission: "Customers" },
  "/sales/getAll": { group: "PosOperations", permission: "SalesHistory" },
  // Exchange (return + replacement in one transaction).
  "/sale/exchange": { group: EXCHANGE_GROUP, permission: EXCHANGE_PERMISSIONS.create },
  "/sales/exchanges": { group: EXCHANGE_GROUP, permission: EXCHANGE_PERMISSIONS.view },
  "/reports/exchange-report": { group: EXCHANGE_GROUP, permission: EXCHANGE_PERMISSIONS.view },
  "/sales/exchange-dashboard": { group: EXCHANGE_GROUP, permission: EXCHANGE_PERMISSIONS.dashboard },
  "/management/exchange-rules": { group: EXCHANGE_GROUP, permission: EXCHANGE_PERMISSIONS.rules },
  // Deals module: the list needs "view"; import / export are gated per-button (and server-side).
  "/management/deals": { group: DEALS_GROUP, permission: DEALS_PERMISSIONS.view },
  "/sales-documents/quotations": { group: "SalesQuotations", permission: "Quotations" },
  "/sales-documents/sales-orders": { group: "SalesQuotations", permission: "SalesOrders" },
  "/sales-documents/invoices": { group: "SalesQuotations", permission: "Invoices" },
  "/sales-documents/returns": { group: "SalesQuotations", permission: "Returns" },
  "/pos/payment-methods": { group: "PosOperations", permission: "PaymentMethods" },
  "/payment-terminal": { group: "PosOperations", permission: "PaymentTerminals" },
  "/user-management": { group: "StaffSecurity", permission: "UserManagement" },
  "/salary-management": { group: "StaffSecurity", permission: "SalaryManagement" },
  "/management/cancellation-access": { group: "StaffSecurity", permission: "CancellationAccess" },
  "/management/void-orders": { group: "StaffSecurity", permission: "VoidOrdersDashboard" },
  // Table Merge/Transfer is a restaurant-tables capability, so it reuses the Tables permission —
  // owners/admins bypass and always see it; anyone permitted to manage tables can toggle it.
  // (Without an entry here the Setup filter hides the card for every non-bypass role.)
  "/management/table-operations": { group: "PosOperations", permission: "TablesRestaurants" },
  "/management/AllAccounts": { group: "FinancialManagement", permission: "ChartOfAccounts" },
  "/management/setup/equity/getall": { group: "FinancialManagement", permission: "TransactionRecords" },
  "/management/setup/owner-transactions": { group: "FinancialManagement", permission: "OwnerTransactions" },
  "/management/setup/transfer-cash": { group: "FinancialManagement", permission: "TransferCash" },
  "/management/setup/cash-management": { group: "CashManagement", permission: "CashManagementView" },
  // Setup → "Send Cash to Branch" card (deep link to the Cash Management floats tab)
  "/management/setup/cash-management?tab=floats": { group: "CashManagement", permission: "CashFloatSend" },
  // Setup → "Opening Balances" card (deep link to the Owner Transactions tab; same permission)
  "/management/setup/owner-transactions?tab=opening-balances": { group: "FinancialManagement", permission: "OwnerTransactions" },
  "/expense": { group: "FinancialManagement", permission: "ExpenseManagement" },
  "/management/ledger": { group: "FinancialManagement", permission: "LedgerManagement" },
  "/management/setup/fbr-integration": { group: "FinancialManagement", permission: "FbrPakistan" },
  "/management/setup/tax-compliance": { group: "FinancialManagement", permission: "FbrPakistan" },
  "/management/setup/zatca-integration": { group: "FinancialManagement", permission: "ZatcaSaudiArabia" },
  "/management/setup/vat-rates": { group: "FinancialManagement", permission: "VatRates" },
  "/management/setup/item-discount-rules": { group: "RulesDiscounts", permission: "ItemDiscountRules" },
  "/management/setup/item-tax-rules": { group: "RulesDiscounts", permission: "ItemTaxRules" },
  "/management/setup/customer-discount-rules": { group: "RulesDiscounts", permission: "CustomerDiscountRules" },
  "/management/setup/invoice-discount-rules": { group: "RulesDiscounts", permission: "InvoiceDiscountRules" },
  "/management/setup/invoice-tax-rules": { group: "RulesDiscounts", permission: "InvoiceTaxRules" },
  "/management/setup/tax-on-payment-methods": { group: "RulesDiscounts", permission: "TaxOnPaymentMethods" },
  "/management/setup/payment-method-tax": { group: "RulesDiscounts", permission: "PaymentMethodTax" },
  "/management/setup/service-charges": { group: "RulesDiscounts", permission: "ServiceCharges" },
  "/management/setup/stock-adjustments": { group: "RulesDiscounts", permission: "StockAdjustments" },
  "/reports/purchase-order": { group: "InventoryProducts", permission: "PurchaseOrderGenerator" },
  "/inventory/goods-received": { group: "InventoryProducts", permission: "GoodsReceivedNote" },
  // Barcode Stock Audit. The list screen only needs "view"; scan / review / approve /
  // finalize / cancel are gated per-button inside the pages (and again server-side).
  "/inventory/stock-audit": { group: "StockAudit", permission: "StockAuditView" },
  // Stock Manual Consumption. The list only needs "view"; create / edit / cancel / delete are
  // gated per-button inside the pages (and again server-side).
  "/inventory/stock-consumption": { group: "StockConsumption", permission: "StockConsumptionView" },
  "/reports/stock-consumption": { group: "StockConsumption", permission: "StockConsumptionView" },
  "/reports": { group: "Reports", permission: "ReportsHub" },
  "/management/setup/feedback": { group: "FeedbackSupport", permission: "FeedbackDashboard" },
  "/management/setup/supporting-applications": {
    group: "SupportingApplications",
    permission: "SupportingApplicationsDownload",
  },
};

const _setupPermDedup = new Map<string, { group: string; permission: string }>();
for (const p of Object.values(SETUP_ROUTE_PERMISSION)) {
  _setupPermDedup.set(`${p.group}\0${p.permission}`, p);
}
_setupPermDedup.set(
  `${SETUP_POS_TERMINAL_PERM.group}\0${SETUP_POS_TERMINAL_PERM.permission}`,
  SETUP_POS_TERMINAL_PERM
);

/** Distinct permissions represented on the setup dashboard (for sidebar “Setup” visibility). */
export const SETUP_HUB_DISTINCT_PERMISSIONS: ReadonlyArray<{ group: string; permission: string }> = [
  ..._setupPermDedup.values(),
];

export function resolveSetupCardPermission(card: {
  path: string | null;
  setupCardKey?: string;
}): { group: string; permission: string } | null {
  if (card.setupCardKey === "pos-terminal") return SETUP_POS_TERMINAL_PERM;
  if (card.path && SETUP_ROUTE_PERMISSION[card.path]) return SETUP_ROUTE_PERMISSION[card.path];
  return null;
}

/**
 * Can the signed-in user open this Setup-hub route? Same rule as the hub's cards (bypass roles,
 * else the mapped catalog permission; unmapped routes are open). For cross-links between modules.
 */
export function canOpenSetupRoute(path: string): boolean {
  const { lookup, bypass } = getNavPermissionContext();
  const perm = SETUP_ROUTE_PERMISSION[path];
  if (!perm || bypass) return true;
  return hasPermission(lookup, perm.group, perm.permission);
}

/** Sidebar / search: combine role checks (caller) with catalog permissions. */
export function canSeeNavMenuItem(
  item: {
    cloudPosPermission?: CloudPosPermSpec;
    requiresSetupHubAccess?: boolean;
  },
  lookup: Map<string, Set<string>>,
  bypass: boolean
): boolean {
  if (item.requiresSetupHubAccess) {
    return canAccessSetupHub(lookup, bypass, SETUP_HUB_DISTINCT_PERMISSIONS);
  }
  return meetsCloudPosPermission(lookup, bypass, item.cloudPosPermission);
}

/** Role names that always get full menu access (company owner / admin). Matches the bypass set. */
const OWNER_BYPASS_ROLE_NAMES = ['developer', 'storeowner', 'companyadmin'];

function rolesIncludeLower(roles: ReadonlyArray<string>, targetLower: string): boolean {
  return roles.some((r) => r.trim().toLowerCase() === targetLower);
}

/** A company owner / admin who must always see every (non-developer) menu item. */
export function isOwnerLikeRole(roles: ReadonlyArray<string>): boolean {
  return OWNER_BYPASS_ROLE_NAMES.some((t) => rolesIncludeLower(roles, t));
}

/**
 * Single source of truth for menu-item visibility, used by the sidebar, global search and the
 * post-login redirect so all three agree. Rules:
 *  - Developer-only items stay restricted to actual Developers (never exposed to company owners).
 *  - Company owners/admins (StoreOwner / CompanyAdmin / Developer) always see every non-developer item.
 *  - Items carrying a catalog permission (cloudPosPermission / requiresSetupHubAccess) are shown when
 *    that permission is granted OR the legacy role-name whitelist matches — so standard roles never
 *    regress and custom company roles gain access purely via the permissions they were granted.
 *  - Items without a catalog permission fall back to the role-name whitelist.
 */
export function passesMenuAccessGate(
  item: {
    roles: string[];
    cloudPosPermission?: CloudPosPermSpec;
    requiresSetupHubAccess?: boolean;
  },
  roles: ReadonlyArray<string>,
  lookup: Map<string, Set<string>>,
  bypass: boolean
): boolean {
  const isDeveloperItem = item.roles.length === 1 && item.roles[0] === 'Developer';
  if (isDeveloperItem) return rolesIncludeLower(roles, 'developer');

  if (isOwnerLikeRole(roles)) return true;

  const roleOk = item.roles.some((role) => roles.includes(role));
  if (item.cloudPosPermission || item.requiresSetupHubAccess) {
    return canSeeNavMenuItem(item, lookup, bypass) || roleOk;
  }
  return roleOk;
}

export function getNavPermissionContext(): {
  lookup: Map<string, Set<string>>;
  bypass: boolean;
} {
  const parsed = parsePermissionGroupsFromStorage();
  const bypass = shouldBypassCloudPosPermissionChecks(parsed);
  const lookup =
    parsed && parsed.length > 0 ? buildPermissionLookup(parsed) : new Map<string, Set<string>>();
  return { lookup, bypass };
}
